import { flatten, unflatten } from 'flat';
import { v4 as uuidv4 } from 'uuid';
import type { WebsocketProvider } from 'y-websocket';
import { IndexeddbPersistence } from 'y-indexeddb';
import * as Y from '../../shared/yjs';
import { buildIDBKey } from '../idb/idbKeyManager';
import { idbManifest } from '../idb/idbManifest';
import { registerOpenIDB, unregisterOpenIDB } from '../idb/idbUtils';
import { shutdownCoordinator } from '../idb/shutdownCoordinator';
import { wsClient, type WSClientHandle } from '../yjs/wsClientSingleton';
import { getDocUpdateLogRegistry } from '../yjs/docUpdateLogRegistry';
import { encodeDocViaWorkerBestEffort } from '../yjs/encodeViaWorker';
import { evaluateLocalSize, clearBloatedIdb, isCorruptDecodeError, logBloatWarning } from '../yjs/idbBloatDetector';
import { onHardStop, isHardStopped, type HardStopEvent } from '../yjs/wsHardStop';
import { IDB_CONNECT_FAILSAFE_MS } from '../yjs/idbConnectPolicy';
import { trackIdbLoad, untrackIdbLoad, onIdbCorruptionCleared } from '../yjs/idbCorruptionTracker';

import { createFileLogger } from '../../utils/logger';
import { docEventLog } from './docEventLog';

const log = createFileLogger('CRDTSERVICEWS_SERVICE', { enabled: false });

export interface CRDTUpdate<T> {
  type: string;
  data: T;
  timestamp: number;
}

// Special known CRDT keys and their preferred debounce times
const CRDT_KEY_CONFIGS: Record<string, number> = {
  'default': 500,           // Default debounce time (ms)
  'cursor': 0,              // Cursor data: dispatch immediately — trailing debounce causes cursors to freeze during movement
  'presence': 0,            // Presence data: dispatch immediately for real-time cursor positions
  'window': 80,             // Window movements: debounce rapid drag events
  'window-position': 80,    // Window position updates
  'window-positions': 80,   // Window position updates (new format)
  'position': 80,           // Position data: matches window debounce
};

// Private instance variable
let _instance: CRDTServiceWS | null = null;

export class CRDTServiceWS {
  private docs = new Map<string, Y.Doc>();
  private providers = new Map<string, WebsocketProvider>();
  private wsHandles = new Map<string, WSClientHandle>();
  private connections: Map<string, (update: any) => void> = new Map();
  private observers: Map<string, { observer: any; cleanup: () => void }> = new Map();
  private states = new Map<string, Y.Map<any>>();
  private localOriginId = uuidv4();
  // IndexedDB persistence for space: rooms (offline-capable window layout)
  private indexedDbs = new Map<string, IndexeddbPersistence>();
  // Per-key callbacks registered by higher-level services (e.g. docsCRDTService).
  // When a Y.Doc emits a decoding error, the registered handler is called to
  // handle full teardown + store notification. This keeps crdtServiceWS passive —
  // it detects the error but the domain layer decides what to do about it.
  private errorHandlers = new Map<string, (error: Error) => void>();

  public registerErrorHandler(key: string, handler: (error: Error) => void): void {
    this.errorHandlers.set(key, handler);
  }

  public unregisterErrorHandler(key: string): void {
    this.errorHandlers.delete(key);
  }

  constructor() {
    // Flush phase: force each open IndexedDB persistence to write the latest
    // in-memory Y.Doc state before the page tears down. This closes the window
    // where a rename committed milliseconds before a refresh is in-flight in
    // IndexeddbPersistence's internal transaction queue but not yet durable.
    // `storeUpdate` is the y-indexeddb internal method that serialises the
    // current doc state into IDB; if it's absent (future version change) we
    // skip silently rather than throw.
    shutdownCoordinator.register({
      name: 'crdtServiceWS:flush',
      phase: 'flush',
      handler: () => {
        this.indexedDbs.forEach((idb) => {
          try {
            const storeUpdate = (idb as any).storeUpdate ?? (idb as any)._storeUpdate;
            if (typeof storeUpdate === 'function') {
              const doc = (idb as any).doc as Y.Doc | undefined;
              if (doc) {
                const update = Y.encodeStateAsUpdateV2(doc);
                storeUpdate.call(idb, update);
              }
            }
          } catch {
            // Non-fatal: IDB connection may already be closing
          }
        });
      },
    });

    shutdownCoordinator.register({
      name: 'crdtServiceWS:destroy',
      phase: 'destroy',
      handler: () => {
        try { this.cleanup(); } catch {}
      }
    });

    // Auto-recover from hard-stops caused by oversized / bloated local state.
    // After a hard-stop the IDB is cleared by crdtServiceWS's stale-size check,
    // but the in-memory Y.Doc still holds the bloated state. After 65 s (long
    // enough for the server-side circuit breaker to expire) we evict the
    // in-memory doc so the next editor access re-creates a fresh empty Y.Doc
    // that connects cleanly from server canonical state.  The registered error
    // handler (if any) triggers the domain layer's editor-rebuild flow.
    //
    // 'size_budget' is no longer guaranteed permanent: `wsSizeBudget` verifies
    // its own trips off-thread and, on a false positive (update-log drift —
    // see wsSizeBudget.ts), clears the hard-stop and reconnects within ~1s —
    // long before this 65s timer fires. Re-check `isHardStopped` right before
    // evicting so a room that self-healed isn't needlessly torn down out from
    // under an actively-synced editor.
    onHardStop((event: HardStopEvent) => {
      if (!event.provider) return;
      if (event.cause !== 'oversized_payload' && event.cause !== 'size_budget') return;

      const provider = event.provider;
      let roomKey: string | null = null;
      for (const [k, p] of this.providers.entries()) {
        if (p === provider) { roomKey = k; break; }
      }
      if (!roomKey) return;

      const key = roomKey;
      log.warn(`[CRDT-WS] Hard-stop (cause=${event.cause}) for ${key.slice(-8)}, scheduling auto-recovery in 65s`);

      const timer = setTimeout(() => {
        if (!this.providers.has(key) && !this.docs.has(key)) return;
        if (!isHardStopped(provider)) {
          log.warn(`[CRDT-WS] Auto-recovery skipped for ${key.slice(-8)} — hard-stop was already cleared (self-healed)`);
          return;
        }
        log.warn(`[CRDT-WS] Auto-recovery: evicting in-memory state for ${key.slice(-8)} so next open starts fresh`);
        docEventLog.log(key, 'hard-stop-auto-recovery', 'evicting in-memory doc after 65s');
        const handler = this.errorHandlers.get(key);
        this.disconnect(key);
        if (handler) {
          try { handler(new Error(`hard-stop auto-recovery: ${event.cause}`)); } catch {}
        }
      }, 65_000);
      (timer as unknown as { unref?: () => void }).unref?.();
    });

    // Recover from corrupt-IDB decode failures. y-indexeddb replays
    // persisted updates through `Y.applyUpdate` on load; a malformed/
    // truncated update throws inside an internal promise chain that has no
    // `.catch()` anywhere in the library, so it surfaces only as a global
    // `unhandledrejection` — `whenSynced` simply never resolves and no
    // 'error' event is ever emitted. Detection + room correlation + IDB
    // clearing is centralized in `idbCorruptionTracker` (shared with
    // localFirstSyncService's entity docs) so a single `idbLoading` map
    // covers every IndexedDB load in the app — splitting that tracking per
    // service would let a corrupt load owned by one misattribute to an
    // unrelated, healthy room owned by the other. We only react here if the
    // cleared key is actually one of ours.
    onIdbCorruptionCleared((roomId, _idb, corruptReason) => {
      if (!this.providers.has(roomId) && !this.docs.has(roomId)) return;
      docEventLog.log(roomId, 'idb-corrupt-detected', corruptReason);
      // y-indexeddb's `Y.transact(doc, () => updates.forEach(applyUpdate))` applies
      // updates one at a time and throws mid-loop, so the shared in-memory Y.Doc may
      // already carry partially-applied corrupt state — evict it entirely (same as
      // the hard-stop auto-recovery above) rather than trying to salvage it in place.
      docEventLog.log(roomId, 'idb-corrupt-cleared', 'evicting in-memory doc so next open re-syncs from server');
      const handler = this.errorHandlers.get(roomId);
      this.disconnect(roomId);
      if (handler) {
        try { handler(new Error(`idb-corrupt-cleared: ${corruptReason}`)); } catch {}
      }
    });
  }

  public getYDoc(key: string): Y.Doc | undefined {
    return this.docs.get(key);
  }

  /**
   * Ensures the Y.Doc for `key` is initialized (creates it if needed, attaches IDB persistence)
   * and waits for IndexedDB to finish loading its persisted state before resolving.
   * For rooms without IDB persistence this resolves immediately. Bounded by
   * IDB_CONNECT_FAILSAFE_MS — y-indexeddb's `whenSynced` never rejects and
   * never resolves on a corrupt/stalled load, so without this timeout a
   * caller awaiting this method could hang forever.
   */
  public waitForIdbSynced(key: string, timeoutMs: number = IDB_CONNECT_FAILSAFE_MS): Promise<void> {
    this.ensureInitialized(key);
    const idb = this.indexedDbs.get(key);
    if (!idb) return Promise.resolve();
    return new Promise((resolve) => {
      let settled = false;
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        log.warn(`[CRDT-WS] waitForIdbSynced: ${key.slice(-8)} did not sync within ${timeoutMs}ms — proceeding without it`);
        resolve();
      }, timeoutMs);
      (idb as any).whenSynced.then(() => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve();
      });
    });
  }

  /**
   * Waits for the room's WebSocket provider to complete a full sync round-trip
   * with the server (`provider.synced === true`), as opposed to
   * `waitForIdbSynced` which only waits for the local IndexedDB load. Resolves
   * `true` once confirmed synced, or `false` if `timeoutMs` elapses first
   * (offline, stalled handshake, no provider for this key, etc.) — callers
   * should treat `false` as "server-confirmed state is not yet available".
   */
  public waitForSynced(key: string, timeoutMs: number = IDB_CONNECT_FAILSAFE_MS): Promise<boolean> {
    const provider = this.providers.get(key);
    if (!provider) return Promise.resolve(false);
    if (provider.synced) return Promise.resolve(true);
    return new Promise((resolve) => {
      let settled = false;
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        log.warn(`[CRDT-WS] waitForSynced: ${key.slice(-8)} did not confirm sync within ${timeoutMs}ms`);
        resolve(false);
      }, timeoutMs);
      provider.once('sync', (isSynced: boolean) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(isSynced);
      });
    });
  }

  public ensureInitialized(key: string, options?: { connectImmediately?: boolean }): Y.Doc {
    let doc = this.docs.get(key);
    let needsProvider = false;
    
    if (!doc) {
      doc = new Y.Doc();
      this.docs.set(key, doc);
      needsProvider = true;
      docEventLog.log(key, 'ydoc-created', `clientID=${doc.clientID}`);
      log.log(`[CRDT-WS] Created new Y.Doc for key ${key}. Totals => docs:${this.docs.size}, providers:${this.providers.size}`);
    } else {
      needsProvider = !this.providers.has(key);
      log.log(`[CRDT-WS] Reusing existing Y.Doc for key ${key}. Provider exists:${!needsProvider}. Totals => docs:${this.docs.size}, providers:${this.providers.size}`);
    }

    // Create provider if needed (either new doc or existing doc without provider)
    if (needsProvider) {
      const roomId = key;
      const connectImmediately = options?.connectImmediately ?? true;

      // ── IndexedDB persistence for space:, space-chat:, doc:, note:, terminal: rooms ──
      // Adds the offline layer so layout, per-user tab state, and document content
      // survive reload, tab close, and crashes. The WebSocket provider starts
      // DISCONNECTED and only connects after IndexedDB has loaded local state —
      // without this the provider broadcasts an empty Y.Doc to the server before IDB
      // has synced, which can overwrite persisted content.
      // space-chat:{spaceId}:{userId} is per-user so IDB keys are automatically
      // isolated (the full room name, including userId, is used as the IDB key).
      const needsIdb = !this.indexedDbs.has(roomId) && (
        roomId.startsWith('space:') || roomId.startsWith('space-chat:') ||
        roomId.startsWith('doc:') || roomId.startsWith('note:') || roomId.startsWith('terminal:')
      );
      if (needsIdb) {
        const idbType = (roomId.startsWith('space:') || roomId.startsWith('space-chat:'))
          ? 'space-layout' as const
          : 'room-crdt' as const;
        const idbKey = buildIDBKey(idbType, roomId);
        const idb = new IndexeddbPersistence(idbKey, doc);
        this.indexedDbs.set(roomId, idb);
        trackIdbLoad(roomId, idb);
        registerOpenIDB(idbKey);

        const entityId = roomId.includes(':') ? roomId.split(':')[1] : roomId;
        idbManifest.register(idbKey, idbType, entityId);
      }

      // Rooms with IDB must start disconnected; all other rooms use the
      // caller-supplied connectImmediately option.
      const shouldConnectImmediately = needsIdb ? false : connectImmediately;
      
      try {
        const wsHandle = wsClient.open({
          roomname: roomId,
          doc,
          connect: shouldConnectImmediately,
          resyncInterval: 30_000,
          // Default maxBackoffTime is 2500ms — too aggressive during worker cycling.
          // With rapid successive drops the backoff counter resets on every successful open,
          // keeping retries at 100ms intervals and hammering the backend. 30s cap prevents this.
          maxBackoffTime: 30_000,
        });
        const provider = wsHandle.provider;

        // Connect only after IndexedDB has loaded local content to avoid
        // broadcasting empty state to the server before IDB has synced.
        if (needsIdb) {
          const idb = this.indexedDbs.get(roomId)!;
          // Must match the idbType used at registration above.
          const idbKey = buildIDBKey(
            (roomId.startsWith('space:') || roomId.startsWith('space-chat:')) ? 'space-layout' : 'room-crdt',
            roomId
          );
          // ── Stale-IDB detection (off-thread) ─────────────────────────
          // Both the pre-WS-sync and post-WS-sync size measurements were
          // historically synchronous `Y.encodeStateAsUpdateV2(doc)` calls
          // on the main thread. For users with very large local Y.Docs
          // (the chandler-pattern overflow case: 64 MiB org-doc), each
          // encode froze the UI for >1 second. Now we route both encodes
          // through the Yjs Web Worker via the per-doc update log.
          //
          // Trade-off: the FIRST `getOrCreate(doc)` call for this doc
          // pays a one-time main-thread seed encode. After that, every
          // measurement is off-thread. For brand-new docs (just created
          // by IDB load), the seed is on a small/empty doc and is
          // negligible. For huge legacy docs, the seed pays the same cost
          // we already paid before — but it happens once per doc lifetime
          // rather than twice per WS connection cycle.
          const docLog = getDocUpdateLogRegistry().getOrCreate(doc);
          // When the caller passes connectImmediately:false it owns the connect
          // lifecycle and will call connectProvider() itself once the doc is
          // hydrated from its source of truth (e.g. docsCRDTService applies
          // entities.skeleton.base64 BEFORE connecting). Auto-connecting here on
          // idb 'synced' would connect BEFORE that hydration when IndexedDB is
          // empty/stale, broadcasting an empty doc that overwrites real content.
          // Default callers (no option) keep the auto-connect-after-idb behavior.
          const autoConnectOnIdbSync = connectImmediately;
          let preWsSyncSize = 0;
          idb.once('synced', () => {
            untrackIdbLoad(roomId);
            idbManifest.touchLastAccessed(idbKey);
            if (autoConnectOnIdbSync) {
              log.log(`[CRDT] 📦 IndexedDB synced for ${roomId.slice(-8)}, connecting WebSocket`);
              // Connect immediately. The encode below runs in parallel on
              // the worker thread; it doesn't gate the WS handshake.
              provider.connect();
            } else {
              log.log(`[CRDT] 📦 IndexedDB synced for ${roomId.slice(-8)}, deferring WS connect to domain layer (post-hydration)`);
            }
            const t0 = performance.now();
            void encodeDocViaWorkerBestEffort(docLog, {
              logTag: `[CRDT staleCheck idb.synced ${roomId.slice(0, 28)}]`,
            }).then((bytes) => {
              preWsSyncSize = bytes?.byteLength ?? 0;
              console.log(
                `[CRDT staleCheck] idb.synced encodeV2 ${roomId.slice(0, 28)}: ` +
                `${(performance.now() - t0).toFixed(1)}ms (worker), size=${preWsSyncSize}b`,
              );
              docEventLog.log(key, 'idb-synced', `stateBytes=${preWsSyncSize} → connecting WS`);

              // ── IDB bloat recovery (chandler-pattern reload loop) ────
              // If the IDB-loaded state already exceeds the per-prefix
              // hard limit, the server will never accept it. wsSizeBudget
              // will hard-stop the in-memory provider, but next page load
              // will reload the same bloated state and fail again. Clear
              // IDB now so the next reload starts from server canonical
              // state.
              const evalResult = evaluateLocalSize(roomId, preWsSyncSize);
              if (evalResult.status === 'clear') {
                docEventLog.log(
                  key,
                  'idb-bloat-cleared',
                  `${preWsSyncSize}b >= hard=${evalResult.hardLimitBytes}b — clearing IDB`,
                );
                void clearBloatedIdb(roomId, idb as never, evalResult);
              } else if (evalResult.status === 'warn') {
                logBloatWarning(roomId, evalResult);
              }
            });
          });

          // After WS sync, check if the merged doc grew significantly beyond
          // what IDB had. Large growth means IDB was very stale (e.g. user
          // was offline while others rewrote the document). In that case,
          // clear and re-seed IDB so it doesn't accumulate divergent
          // tombstone history.
          let staleCheckDone = false;
          provider.once('sync', () => {
            if (staleCheckDone) return;
            staleCheckDone = true;
            const t0 = performance.now();
            void encodeDocViaWorkerBestEffort(docLog, {
              logTag: `[CRDT staleCheck ws.sync ${roomId.slice(0, 28)}]`,
            }).then((bytes) => {
              const postSyncSize = bytes?.byteLength ?? 0;
              console.log(
                `[CRDT staleCheck] ws.sync encodeV2 ${roomId.slice(0, 28)}: ` +
                `${(performance.now() - t0).toFixed(1)}ms (worker), ` +
                `${preWsSyncSize}b → ${postSyncSize}b`,
              );
              const growth = postSyncSize - preWsSyncSize;
              const growthRatio = preWsSyncSize > 0 ? postSyncSize / preWsSyncSize : 1;
              if (growthRatio > 3 && growth > 50000) {
                log.warn(`[CRDT] IDB stale for ${roomId.slice(-8)}: ${preWsSyncSize}b → ${postSyncSize}b (${growthRatio.toFixed(1)}x). Clearing IDB to reseed.`);
                docEventLog.log(key, 'idb-stale-cleared', `${preWsSyncSize}b → ${postSyncSize}b (${growthRatio.toFixed(1)}x growth), clearing IDB`);
                void (idb as any).clearData();
              } else {
                docEventLog.log(key, 'ws-first-sync', `${preWsSyncSize}b → ${postSyncSize}b`);
              }
            }).catch((err) => {
              log.error(`[CRDT] Stale IDB check failed for ${roomId.slice(-8)}:`, err);
            });
          });

          // Absolute connect failsafe: if the provider is still not connected
          // IDB_CONNECT_FAILSAFE_MS after creation (IDB stalled, or the domain
          // layer never called connectProvider), force a connect() so
          // shouldConnect=true and the watchdog can take over.  Safe to call
          // even if connect was already issued — y-websocket's connect() is
          // idempotent and this no-ops when shouldConnect is already true.
          const _connectFailsafe = setTimeout(() => {
            if (!this.providers.has(roomId)) return;
            const p = this.providers.get(roomId)!;
            if (!(p as any).shouldConnect) {
              log.warn(`[CRDT-WS] Connect failsafe: ${roomId.slice(-8)} — forcing connect after ${IDB_CONNECT_FAILSAFE_MS}ms (IDB or domain timed out)`);
              docEventLog.log(key, 'connect-failsafe', `absolute ${IDB_CONNECT_FAILSAFE_MS}ms timer`);
              p.connect();
              // Give up on tracking this load for corruption correlation too —
              // if IDB hasn't synced by now it's either genuinely stuck (quota,
              // private mode) or slow enough that leaving it "in flight"
              // indefinitely would permanently poison resolveCorruptCandidate's
              // single-candidate heuristic for every OTHER room opened for the
              // rest of the session. idb.once('synced') above is unaffected —
              // if it fires later, untrackIdbLoad is idempotent.
              untrackIdbLoad(roomId);
            }
          }, IDB_CONNECT_FAILSAFE_MS);
          (_connectFailsafe as unknown as { unref?: () => void }).unref?.();
        }
        
        // Single unified status handler
        provider.on('status', ({ status }: { status: string }) => {
          if (roomId.startsWith('doc:')) {
            const yxml = doc.getXmlFragment('prosemirror');
            log.log(`[CRDT-WS] ${roomId.slice(-8)} ${status} elements=${yxml.length}`);
          }
          if (status === 'connected') {
            if (provider.ws) provider.ws.binaryType = 'arraybuffer';
            log.log(`[CRDT-WS] Connected to ${roomId}`);
            docEventLog.log(key, 'ws-connected', `yxml=${doc.getXmlFragment('prosemirror').length} elements`);
          } else if (status === 'disconnected') {
            log.warn(`[CRDT-WS] Disconnected from ${roomId}, will auto-reconnect`);
            docEventLog.log(key, 'ws-disconnected', '');
          }
        });

        // Set binaryType immediately on creation (before first status event)
        if (provider.ws) provider.ws.binaryType = 'arraybuffer';
        
        provider.on('connection-error', (event: Event) => {
          log.error(`[CRDT-WS] Connection error for ${roomId}:`, event);
          // The provider will automatically retry connections
        });
        
        provider.on('connection-close', (event: CloseEvent | null) => {
          if (event) {
            log.warn(`[CRDT-WS] Connection closed for ${roomId}:`, event.code, event.reason);
          } else {
            log.warn(`[CRDT-WS] Connection closed for ${roomId}`);
          }
        });
        
        // Y.Doc error handler: stop receiving corrupt frames, then delegate full
        // teardown to the registered domain-layer handler (e.g. docsCRDTService).
        // crdtServiceWS only disconnects — it does NOT evict from its own cache so
        // the handler can call this.disconnect(key) as part of a coordinated cleanup.
        // @ts-expect-error - Y.Doc does support 'error' event but TypeScript types are incomplete
        doc.on('error', (error: Error) => {
          log.error(`[CRDT-WS] 🚨 Y.Doc internal error for ${roomId}:`, error.message);
          docEventLog.log(key, 'ydoc-error', error.message);

          const handler = this.errorHandlers.get(key);
          if (handler) {
            try { provider.disconnect(); } catch {}
            docEventLog.log(key, 'corruption-handler-called', 'delegating to domain layer');
            handler(error);
          } else {
            // No domain handler registered (e.g. non-doc: rooms used directly).
            // Fall back to autonomous eviction — same behaviour as before.
            if (isCorruptDecodeError(error.message)) {
              log.warn(`[CRDT-WS] No error handler registered for ${roomId}, evicting from cache.`);
              try { wsHandle.close(); } catch {}
              this.docs.delete(key);
              this.providers.delete(key);
              this.wsHandles.delete(key);
            }
          }
        });

        this.providers.set(key, provider);
        this.wsHandles.set(key, wsHandle);
        // wsClient.open auto-attaches wsTokenRefresher, wsHardStop, wsSizeBudget
        // and (for known prefixes) wsObservability — no need to wire them by hand.
        log.log(`[CRDT-WS] Created provider for ${roomId}. Totals => docs:${this.docs.size}, providers:${this.providers.size}`);
      } catch (error) {
        log.error(`[CRDT-WS] Failed to create WebSocket provider for ${roomId}:`, error);
        // Continue without WebSocket - the doc will still work locally
      }
    }
    
    log.log(
      `[CRDT-WS] ensureInitialized complete for ${key}: ${JSON.stringify({
        hasDoc: !!doc,
        docClientId: doc.clientID,
        hasProvider: this.providers.has(key),
        totalDocs: this.docs.size,
        totalProviders: this.providers.size,
      })}`,
    );
    
    return doc;
  }

  // Function to get the appropriate debounce time based on key
  private getDebounceTime(key: string): number {
    // Check if the key contains any of our special types
    for (const keyType of Object.keys(CRDT_KEY_CONFIGS)) {
      if (keyType !== 'default' && key.includes(keyType)) {
        return CRDT_KEY_CONFIGS[keyType];
      }
    }
    // Fall back to default
    return CRDT_KEY_CONFIGS.default;
  }

  private debouncedObserver(key: string, ymap: Y.Map<any>, onUpdate: (update: any) => void) {
    // Create a debounced version of the observer that waits a short period before firing
    let timeoutId: any = null;
    let lastUpdate: any = null;
    let cancelled = false;

    // Determine the debounce time based on the key type
    const debounceTime = this.getDebounceTime(key);

    const observer = (event: Y.YMapEvent<any>, transaction: Y.Transaction) => {
      if (cancelled) return;
      if (transaction.origin === this.localOriginId) {
        return;
      }

      const data = this.ymapToObject(ymap);
      if (!data) {
        return;
      }

      lastUpdate = {
        type: 'update',
        data: data,
        timestamp: Date.now(),
      };

      // For zero-debounce items, send immediately
      if (debounceTime === 0) {
        try {
          onUpdate(lastUpdate);
        } catch (error) {
          log.error(`[CRDT-WS] Error in immediate update callback for key: ${key}:`, error);
        }
        return;
      }

      // Clear existing timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Set a new timeout with the appropriate debounce time
      timeoutId = setTimeout(() => {
        timeoutId = null;
        // The observer may have been cancelled while the timer was pending
        // (cleanup races against the timer). Without this check, a stale
        // callback fires AFTER unobserve and writes wrong-space state — the
        // root cause of cross-space window contamination during space
        // switches. Reference: see docs/Kernel/Storage & Sync/YJS_ARCHITECTURE_CANONICAL.md.
        if (cancelled) return;
        try {
          onUpdate(lastUpdate);
        } catch (error) {
          log.error(`[CRDT-WS] Error in debounced update callback for key: ${key}:`, error);
        }
      }, debounceTime);
    };

    // Expose a way for the connect() caller to cancel both the live observer
    // and any pending debounced fire.
    (observer as any).__cancel = () => {
      cancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      lastUpdate = null;
    };

    return observer;
  }

  connect<T>(key: string, onUpdate: (update: CRDTUpdate<T>) => void): () => void {
    const doc = this.ensureInitialized(key);

    const ymap = doc.getMap<T>(key);
    this.states.set(key, ymap);

    const debouncedObserver = this.debouncedObserver(key, ymap, onUpdate);

    ymap.observe(debouncedObserver);

    this.connections.set(key, onUpdate);
    this.observers.set(key, {
      observer: debouncedObserver,
      cleanup: () => {
        ymap.unobserve(debouncedObserver);
        // Cancel any pending debounced fire so it doesn't run after the
        // caller has torn down. See debouncedObserver above.
        const cancel = (debouncedObserver as any).__cancel as undefined | (() => void);
        if (typeof cancel === 'function') cancel();
        this.connections.delete(key);
        this.observers.delete(key);
      },
    });

    return () => {
      const observer = this.observers.get(key);
      if (observer) {
        observer.cleanup();
      }
    };
  }

  update<T extends object>(
    key: string,
    newData: T,
    options: {
      preserveOtherConnections?: boolean;
      preserveNoteConnections?: boolean;
      updateType?: 'update' | 'remove';
    } = {},
  ) {
    try {
      // Use the cached doc directly when available to avoid the full ensureInitialized
      // overhead on hot paths like presence cursor updates (called 10×/sec).
      const doc = this.docs.get(key) ?? this.ensureInitialized(key);
      const ymap = doc.getMap(key);
      const updateMap = this.objectToYmap<T>(newData);

      doc.transact(() => {
        if (options.updateType === 'remove') {
          this.removeMapEntries(ymap, updateMap);
        } else if (options.updateType === 'update') {
          this.updateMap(ymap, updateMap);
        }
      }, this.localOriginId);

      this.states.set(key, ymap);

      if (!options.preserveOtherConnections) {
        this.cleanupObservers(key, options);
      }
    } catch (error) {
      log.error(`[CRDT-WS] Error in update method for key: ${key}:`, error);
      throw error;
    }
  }

  disconnect(key: string) {
    const provider = this.providers.get(key);
    if (provider) {
      try {
        // Clear local awareness state before closing so peers see us leave
        // immediately (wsHandle.close() destroys the provider next, which
        // triggers the awareness change broadcast).
        try { (provider as any).awareness?.setLocalState(null); } catch {}
      } catch {}
      this.providers.delete(key);
    }
    const wsHandle = this.wsHandles.get(key);
    if (wsHandle) {
      try { wsHandle.close(); } catch {}
      this.wsHandles.delete(key);
    }
    const idb = this.indexedDbs.get(key);
    if (idb) {
      try { idb.destroy(); } catch {}
      this.indexedDbs.delete(key);
      untrackIdbLoad(key);
      const idbType = (key.startsWith('space:') || key.startsWith('space-chat:'))
        ? 'space-layout' as const
        : 'room-crdt' as const;
      const idbKey = buildIDBKey(idbType, key);
      unregisterOpenIDB(idbKey);
      idbManifest.unregister(idbKey);
    }
    this.docs.delete(key);
    this.errorHandlers.delete(key);
    docEventLog.log(key, 'ws-evicted', `docs=${this.docs.size} remaining`);
    log.log(`[CRDT-WS] Disconnected and removed key ${key}. Totals => docs:${this.docs.size}, providers:${this.providers.size}`);
  }

  cleanup() {
    // Copy keys to avoid iterator mutation issues
    const keys = Array.from(this.providers.keys());
    for (const key of keys) {
      this.disconnect(key); // disconnect() handles idb.destroy() + idbManifest.unregister()
    }
  }

  public hasIdbForKey(key: string): boolean {
    return this.indexedDbs.has(key);
  }

  // DEBUG HELPERS

  getProvider(key: string): WebsocketProvider | undefined {
    return this.providers.get(key);
  }

  connectProvider(key: string): void {
    const provider = this.providers.get(key);
    if (!provider) return;
    // Skip if already live (connected or actively connecting).
    const isLive = provider.wsconnected || Boolean((provider as any).wsconnecting);
    if (!isLive) {
      provider.connect();
      log.log(`[CRDT-WS] Manually connected provider for ${key}`);
    }
  }

  /**
   * Reconnect a stranded provider by key if it wants a connection but is
   * currently down.  Used by the watchdog and user-interaction gate.
   */
  ensureConnected(key: string): void {
    const provider = this.providers.get(key);
    if (!provider) return;
    const wantsConn = Boolean((provider as any).shouldConnect);
    const isLive = provider.wsconnected || Boolean((provider as any).wsconnecting);
    if (wantsConn && !isLive) {
      provider.connect();
      log.log(`[CRDT-WS] ensureConnected: kicked stranded provider for ${key}`);
    }
  }

  getActiveDocs(): Map<string, Y.Doc> {
    return this.docs;
  }

  getActiveProviders(): Map<string, WebsocketProvider> {
    return this.providers;
  }

  // Emergency cleanup: remove all docs/providers for a specific doc id
  cleanupDocId(docId: string) {
    const key = `doc:${docId}`;
    try {
      this.disconnect(key);
      log.log(`[CRDT-WS] cleanupDocId: force disconnected ${key}`);
    } catch (e) {
      log.error('[CRDT-WS] cleanupDocId error:', e);
    }
  }

  get(key: string) {
    return this.docs.get(key);
  }

  private updateMap(ymap: Y.Map<any>, updateMap: Map<string, any>) {
    for (const [k, v] of updateMap.entries()) {
      try {
        if (ymap.get(k) !== v) {
          ymap.set(k, v);
        }
      } catch (keyError) {
        log.error('[CRDT-WS] map: Error setting key:', k, 'Error:', keyError);
        // Continue with other keys even if one fails
      }
    }
  }

  private removeMapEntries(ymap: Y.Map<any>, updateMap: Map<string, any>) {
    for (const [k, v] of updateMap.entries()) {
      // Special handling for null values - explicitly delete the key
      if (v === null || v === undefined) {
        if (ymap.has(k)) {
          ymap.delete(k);
        }
        continue;
      }
      
      // For other patterns, look for matching keys or patterns
      ymap.forEach((value, mapKey) => {
        if (mapKey === k || mapKey.startsWith(k + '.')) {
          ymap.delete(mapKey);
        }
      });
    }
  }

  private cleanupObservers(key: string, options: { preserveNoteConnections?: boolean }) {
    for (const [connKey, observer] of this.observers.entries()) {
      if (connKey === key || (!options.preserveNoteConnections && !connKey.startsWith('note-'))) {
        observer.cleanup();
      }
    }
  }

  //
  // CRDT YMAP HELPERS
  //
  private ymapToObject<T>(ymap: Y.Map<T>): T | null {
    try {
      const ymapObject = ymap.toJSON();
      if (!ymapObject || typeof ymapObject !== 'object' || Object.keys(ymapObject).length === 0) {
        return null;
      }

      // Deep convert nested Y.js objects to plain JS — toJSON() only converts
      // the top-level Y.Map; nested Y.Maps need explicit recursive conversion.
      const deepConvert = (obj: any): any => {
        if (obj === null || obj === undefined) return obj;
        if (typeof obj !== 'object') return obj;
        if (obj.toJSON && typeof obj.toJSON === 'function') {
          return deepConvert(obj.toJSON());
        }
        if (Array.isArray(obj)) {
          return obj.map(deepConvert);
        }
        const converted: any = {};
        for (const key in obj) {
          if (obj.hasOwnProperty(key)) {
            converted[key] = deepConvert(obj[key]);
          }
        }
        return converted;
      };

      const fullyConverted = deepConvert(ymapObject);
      const normalizedObject = this.removeParentEntries(fullyConverted);
      return unflatten(normalizedObject) as T;
    } catch (error) {
      log.error(`[CRDT-WS] Error in ymapToObject:`, error);
      return null;
    }
  }

  private objectToYmap<T>(object: T) {
    try {
      const flatObject = flatten(object);
      const result = new Map(Object.entries(flatObject as Record<string, any>));
      return result;
    } catch (error) {
      log.error(`[CRDT-WS] Error in objectToYmap:`, error);
      throw error;
    }
  }

  private removeParentEntries(object: any) {
    const keys = Object.keys(object);
    const parentKeys = keys.filter((key) => {
      return keys.some((k) => k.startsWith(key + '.'));
    });
    parentKeys.forEach((key) => {
      delete object[key];
    });
    return object;
  }

  /** Returns a snapshot of all active Y.Doc rooms for the debug panel. */
  public getDebugSnapshot(): Array<{
    room: string;
    wsConnected: boolean;
    wsSynced: boolean;
    wsUrl: string;
    docKeys: string[];
  }> {
    const result: ReturnType<typeof this.getDebugSnapshot> = [];
    for (const [room, doc] of this.docs.entries()) {
      const provider = this.providers.get(room);
      const docKeys: string[] = [];
      doc.share.forEach((_val, key) => docKeys.push(key));
      result.push({
        room,
        wsConnected: provider?.wsconnected ?? false,
        wsSynced: provider?.synced ?? false,
        wsUrl: (provider as any)?.url ?? '',
        docKeys,
      });
    }
    return result;
  }
}

// Export a function to get the singleton instance
export function getCRDTServiceInstance(): CRDTServiceWS {
  if (!_instance) {
    _instance = new CRDTServiceWS();
  }
  return _instance;
}

// Add the missing export that's being imported elsewhere
export const crdtServiceWS = getCRDTServiceInstance();
